import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Why and how I built my own course platform",
  "description": "",
  "published": "2020-02-14T00:00:00.000Z",
  "redirect_from": ["/blog/why-and-how-i-built-my-own-course-platform/swizec/9330"],
  "categories": "Uncategorized",
  "hero": "./img/techletter.app-screenshot-1581701543300.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Rule number 1 of indie hacking: `}<em parentName="p">{`Coding is a trap, focus on your product.`}</em></p>
    <p>{`So why the heck did I build a course platform for `}<a parentName="p" {...{
        "href": "https://serverlessreact.dev"
      }}>{`ServerlessReact.Dev`}</a>{` when services like Teachable, Podia, Thinkific, and such already exist? Because of friction my friend.`}</p>
    <p>{`Lemme explain`}</p>
    <p>{`My goal is to give frontend engineers superpowers and turn them into full-fledged product owners. Whether that product is a feature at work or a great idea for an app.`}</p>
    <p>{`The way I do that is quality content.`}</p>
    <p>{`Thoughtful emails from experience, detailed articles, well-researched video courses, deep dive workshops, and funny gifs. You gotta have gifs.`}</p>
    <p><img parentName="p" {...{
        "src": "/43644f39e0a51c0cd5f1477ac0848946/media-110F1JFzWKtiA8-giphy.gif",
        "alt": "funny_gifs giphy"
      }}></img></p>
    <p>{`Everything else is a distraction. `}<em parentName="p">{`Especially`}</em>{` building a custom course platform.`}</p>
    <p>{`But here's the thing: managing content is `}<em parentName="p">{`also`}</em>{` a distraction. I should focus on `}<em parentName="p">{`creating`}</em>{` content. Doing research, following trends, finding ideas, writing, editing, recording, coding ...`}</p>
    <p>{`What creating shouldn't involve is futzing around with video uploads, getting transcripts, adding subtitles, rescaling images, formatting content, embedding stuff, or even looking for gifs.`}</p>
    <p>{`And that's where other course platforms fail.`}</p>
    <p>{`In appealing to the lowest common denominator they lose the very thing that makes them useful: Removing work, `}<em parentName="p">{`being frictionless`}</em>{`.`}</p>
    <h2 {...{
      "id": "how-a-custom-platform-reduces-friction"
    }}>{`How a custom platform reduces friction`}</h2>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/Swizec/status/1224404079461289984"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "640px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "91.92825112107623%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'367\\'%20viewBox=\\'0%200%20400%20367\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M23%2017c-13%206-8%2027%207%2027%207%200%2013-4%208-6-4-1-3-5%201-5%205%200%206-5%204-10-4-6-13-10-20-6\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/0084dede52c0f6349fe296421f86d1b2/ca0a1/techletter.app-screenshot-1581701543300.webp 223w", "/static/0084dede52c0f6349fe296421f86d1b2/75680/techletter.app-screenshot-1581701543300.webp 445w", "/static/0084dede52c0f6349fe296421f86d1b2/0ba47/techletter.app-screenshot-1581701543300.webp 640w"],
                "sizes": "(max-width: 640px) 100vw, 640px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/0084dede52c0f6349fe296421f86d1b2/e92b6/techletter.app-screenshot-1581701543300.png 223w", "/static/0084dede52c0f6349fe296421f86d1b2/e66bf/techletter.app-screenshot-1581701543300.png 445w", "/static/0084dede52c0f6349fe296421f86d1b2/6af66/techletter.app-screenshot-1581701543300.png 640w"],
                "sizes": "(max-width: 640px) 100vw, 640px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/0084dede52c0f6349fe296421f86d1b2/6af66/techletter.app-screenshot-1581701543300.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`Most course platforms are designed to be `}<em parentName="p">{`simple`}</em>{`, but they're not designed to be `}<em parentName="p">{`easy`}</em>{`. At least not if you're technical or stuck in your ways.`}</p>
    <p>{`I write in markdown. Using iA Writer since 2010 or so.`}</p>
    <p>{`I record in OBS. Often livestreamed.`}</p>
    <p>{`My DSLR is the face camera. When there's no code, I just talk into the DSLR like a vlog.`}</p>
    <p>{`I edit videos in DaVinci Resolve.`}</p>
    <p>{`All serious work should be version controlled with Git. Saved in Dropbox at the very least. It's got version control too.`}</p>
    <p>{`Now, which platform do you think supports that authoring setup my friend?`}</p>
    <p>{`Yep, none of them.`}</p>
    <p><img parentName="p" {...{
        "src": "/d55117e066982e3e1a11180d11139dcd/media-mbPqOzyASJzB6-giphy.gif",
        "alt": "sad_trombone giphy"
      }}></img></p>
    <h2 {...{
      "id": "platforms-and-misaligned-incentives"
    }}>{`Platforms and misaligned incentives`}</h2>
    <p>{`You see, their incentive isn't to make `}<em parentName="p">{`your`}</em>{` life easy. Platforms want to make `}<em parentName="p">{`everyone`}</em>{`'s life simple.`}</p>
    <p>{`Come on Teachable and we've got you covered. Come to Podia and get started. No skills required.`}</p>
    <p>{`Spin up our editor and write your content. Flick on the webcam and record right here. Upload a video and we'll keep it safe for ya.`}</p>
    <p><strong parentName="p">{`Please use our platform for authoring `}<em parentName="strong">{`and`}</em>{` publishing.`}</strong></p>
    <p>{`I've already got a great `}<em parentName="p">{`authoring`}</em>{` flow, thanks. Can I still use your platform?`}</p>
    <p>{`Sure you can, just go through these five bazillion painful steps and we've got you!`}</p>
    <p>{`All these platforms use WYSIWYG. No markdown support. You're supposed to write right there and hit publish.`}</p>
    <p>{`Oh you don't want to write here? Maybe you can copy pasta. Oh wait you'd like to preserve your formatting when you paste? LoL`}</p>
    <p>{`You want to upload videos? Great! Just click these five buttons in the right sequence and we've got you. We'll even host it for ya.`}</p>
    <p>{`Integrate with your video editing software? hahahahahahaha`}</p>
    <p>{`Oh you want version control? What's that? You realize nobody outside of tech even realized that's a thing right? Why on earth would we support that feature don't be silly.`}</p>
    <p>{`Captions? Eh`}</p>
    <p>{`Transcripts? You can copy pasta into a new section can't you?`}</p>
    <p>{`Multiple videos per lesson? That does not fit our philosophy. A/B testing has showed that users want one video at a time and then to click a big fat NEXT button so they get a sense of accomplishment for finishing a lesson.`}</p>
    <p>{`But my videos are short. You have to watch 3 of them to get all the steps of the same overall lesson.`}</p>
    <p><em parentName="p">{`A/B testing has showed`}</em>{` ...`}</p>
    <h2 {...{
      "id": "so-i-built-my-own"
    }}>{`So I built my own`}</h2>
    <p>{`So after spending heaps of money, time, and effort trying to make Teachable easier to work with and `}<em parentName="p">{`absolutely dreading`}</em>{` the pain of making a new course ... `}<strong parentName="p">{`I said fuck it and built my own`}</strong></p>
    <p>{`Now my process looks like this:`}</p>
    <ul>
      <li parentName="ul">{`livestream a build`}</li>
      <li parentName="ul">{`edit into videos in DaVinci Resolve`}</li>
      <li parentName="ul">{`hit render`}</li>
      <li parentName="ul">{`DaVinci uploads to Vimeo`}</li>
      <li parentName="ul">{`find video urls on Vimeo`}</li>
      <li parentName="ul">{`paste into iA Writer`}</li>
      <li parentName="ul">{`add descriptions in Markdown`}</li>
      <li parentName="ul">{`run `}<inlineCode parentName="li">{`now --prod`}</inlineCode>{` to deploy`}</li>
      <li parentName="ul">{`go to Rev.com`}</li>
      <li parentName="ul">{`select Vimeo vids`}</li>
      <li parentName="ul">{`Rev creates captions and delivers straight to Vimeo`}</li>
    </ul>
    <p>{`It. Is. Wonderful. 😍`}</p>
    <p>{`Easiest it's ever been to make a course.`}</p>
    <p>{`And check this my friend: ServerlessReact.Dev is built with Gatsby so adding a new section or module is as easy as creating a new `}<inlineCode parentName="p">{`module.mdx`}</inlineCode>{` file. Hit deploy and voila, new page.`}</p>
    <p>{`I have ideas to make it even more frictionless. Gatsby can read Vimeo's API and handle the videos itself. Maybe even insert transcripts from Rev 🤔`}</p>
    <p>{`Using Gatsby also means that my course renders as a static HTML page. You can use it on your phone, laptop, crappy wifi, or wonderful broadband. Once it's open it's open.`}</p>
    <p>{`And the best part? I can fix things from an airplane.`}</p>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/Swizec/status/1228041748120666112"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "640px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "121.97309417040358%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'488\\'%20viewBox=\\'0%200%20400%20488\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M23%2017c-16%208-4%2034%2013%2026%205-2%205-4%202-5-5-1-3-5%202-5%207%200%205-10-3-15-5-4-8-4-14-1m77%20207c0%205%203%208%204%205h1c0%202%203%201%203-1l1-2v2c0%202%202%203%202%200h1c0%202%201%202%204%202s4%200%204-2h1c0%203%203%202%203-1s-1-3-6-2c-3%201-4%200-6-1h-10l-1-1-1%201m70%200c-1%201-4%202-9%201h-8v2c0%203%202%204%202%201h1l1%202%201-2h1c0%203%2022%203%2022%200h1l1%202%202-2h1l1%202%201-2c0-2-1-2-8-2-5%200-7-1-7-2h-3m40%200c0%201-10%202-12%201h-4c-3%200-4%201-4%203%200%203%205%203%205%200h1c0%202%201%202%209%202%2010%200%2010%200%2010-3l-2-2-1-1-1-1-1%201m-77%2016h-33v57l1%2057h138l63%201V240h-31a9076%209076%200%2001-138%200m57%2062c-7%206-9%2012-5%2021%207%2015%2030%209%2030-8%200-9-5-14-15-16-5-1-5-1-10%203m0%205c-3%203-3%204-3%208%200%2014%2018%2018%2023%205s-10-23-20-13\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/6c039866ec2700027a8d85e253108368/ca0a1/techletter.app-screenshot-1581701520345.webp 223w", "/static/6c039866ec2700027a8d85e253108368/75680/techletter.app-screenshot-1581701520345.webp 445w", "/static/6c039866ec2700027a8d85e253108368/0ba47/techletter.app-screenshot-1581701520345.webp 640w"],
                "sizes": "(max-width: 640px) 100vw, 640px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/6c039866ec2700027a8d85e253108368/e92b6/techletter.app-screenshot-1581701520345.png 223w", "/static/6c039866ec2700027a8d85e253108368/e66bf/techletter.app-screenshot-1581701520345.png 445w", "/static/6c039866ec2700027a8d85e253108368/6af66/techletter.app-screenshot-1581701520345.png 640w"],
                "sizes": "(max-width: 640px) 100vw, 640px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/6c039866ec2700027a8d85e253108368/6af66/techletter.app-screenshot-1581701520345.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`🤘`}</p>
    <p>{`Cheers,`}<br parentName="p"></br>{`
`}{`~Swizec`}</p>
    <p>{`PS: this post got long so maybe I'll write about the Gumroad-> Auth0 -> useAuth -> course integration next time. Interested? hit reply`}</p>
    <p>{`PPS: Gumroad because I'm not dumb enough to build my own checkout ;)`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      